import { useEffect, useMemo, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { loadCache, saveCache } from "@/lib/localCache";

// kg ↔ display-unit conversions
const KG_PER_LB = 0.45359237;
const KG_PER_ST = 6.35029318;
const toDisplay = (kg, unit) => {
  if (unit === "lb") return kg / KG_PER_LB;
  if (unit === "st") return kg / KG_PER_ST;
  return kg;
};
const fromDisplay = (val, unit) => {
  const v = Number(val) || 0;
  if (unit === "lb") return v * KG_PER_LB;
  if (unit === "st") return v * KG_PER_ST;
  return v;
};
const formatDisplay = (kg, unit) => {
  if (kg == null) return "—";
  const v = toDisplay(kg, unit);
  if (unit === "kg") return `${v.toFixed(1)} kg`;
  if (unit === "lb") return `${v.toFixed(1)} lb`;
  // stones: show as "X st Y.Y lb" for readability
  const wholeSt = Math.floor(v);
  const remainderLb = (v - wholeSt) * 14;
  return `${wholeSt} st ${remainderLb.toFixed(1)} lb`;
};
const formatDelta = (kg, unit) => {
  if (kg == null) return "—";
  const sign = kg > 0 ? "+" : kg < 0 ? "−" : "";
  const abs = Math.abs(kg);
  if (unit === "kg") return `${sign}${abs.toFixed(1)} kg`;
  if (unit === "lb") return `${sign}${(abs / KG_PER_LB).toFixed(1)} lb`;
  return `${sign}${(abs / KG_PER_ST).toFixed(2)} st`;
};

const todayDateStr = () => new Date().toISOString().slice(0, 10);

export default function WeightTracker() {
  const [unit, setUnit] = useState(() => localStorage.getItem("ff_weight_unit") || "kg");
  const [summary, setSummary] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGoal, setShowGoal] = useState(false);

  useEffect(() => { localStorage.setItem("ff_weight_unit", unit); }, [unit]);

  const refresh = async () => {
    try {
      const [s, l] = await Promise.all([
        api.get("/weight-log/summary"),
        api.get("/weight-log?days=365"),
      ]);
      setSummary(s.data);
      setLogs(l.data.logs || []);
      saveCache("weight_data_v1", { summary: s.data, logs: l.data.logs || [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load weight data");
    }
  };

  useEffect(() => {
    // Hydrate from cache first so returning users see the chart instantly.
    const cached = loadCache("weight_data_v1", 60 * 60 * 1000); // 1 hour TTL
    if (cached?.summary && Array.isArray(cached.logs) && cached.logs.length) {
      setSummary(cached.summary);
      setLogs(cached.logs);
      setLoading(false);
    }
    (async () => { try { await refresh(); } finally { setLoading(false); } })();
  }, []);

  const chartData = useMemo(() => logs.map((l) => ({
    date: l.logged_at.slice(0, 10),
    value: Number(toDisplay(l.weight_kg, unit).toFixed(2)),
    raw: l,
  })), [logs, unit]);

  if (loading) return <div className="text-[#5C6B61] p-6">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="weight-tracker">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="ff-label">Weight loss journey</div>
          <h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
            Weight tracker
          </h1>
          <p className="text-sm text-[#5C6B61] mt-1 max-w-xl">
            Log your weight whenever you remember. We&apos;ll show your progress and projected goal date.
          </p>
        </div>
        <UnitPicker unit={unit} setUnit={setUnit} />
      </header>

      <StatsRow summary={summary} unit={unit} onSetGoal={() => setShowGoal(true)} />

      <LogForm unit={unit} onLogged={refresh} />

      {chartData.length >= 2 ? (
        summary?.is_pro ? (
          <div className="ff-card p-4 md:p-6" data-testid="weight-chart-card">
            <div className="ff-label mb-3">Progress chart</div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
                  <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 11, fill: "#5C6B61" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#5C6B61" }} domain={["auto", "auto"]} />
                  <Tooltip
                    contentStyle={{ borderRadius: 12, border: "1px solid #E5E2DA", fontSize: 12 }}
                    formatter={(v) => [`${v} ${unit}`, "Weight"]}
                  />
                  {summary?.goal_kg && (
                    <ReferenceLine
                      y={Number(toDisplay(summary.goal_kg, unit).toFixed(2))}
                      stroke="#C36952"
                      strokeDasharray="4 4"
                      label={{ value: "Goal", fontSize: 10, fill: "#C36952", position: "right" }}
                    />
                  )}
                  <Line type="monotone" dataKey="value" stroke="#3B5A3E" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          <ProLockedChartCard />
        )
      ) : (
        <div className="ff-card p-6 text-center text-sm text-[#5C6B61]" data-testid="weight-chart-empty">
          Log at least two entries to see your progress chart.
        </div>
      )}

      <HistoryList logs={logs} unit={unit} onChanged={refresh} />

      <WhatsWorkingCard unit={unit} />

      {showGoal && (
        <GoalModal
          current={summary?.goal_kg}
          unit={unit}
          onClose={() => setShowGoal(false)}
          onSaved={refresh}
        />
      )}
    </div>
  );
}

function UnitPicker({ unit, setUnit }) {
  const units = [{ id: "kg", label: "kg" }, { id: "lb", label: "lb" }, { id: "st", label: "stones" }];
  return (
    <div className="inline-flex rounded-full bg-white border border-[#E5E2DA] p-1" data-testid="weight-unit-picker">
      {units.map((u) => (
        <button
          key={u.id}
          onClick={() => setUnit(u.id)}
          data-testid={`unit-${u.id}`}
          className={`px-3 py-1 text-xs font-semibold rounded-full transition ${unit === u.id ? "bg-[#3B5A3E] text-white" : "text-[#5C6B61] hover:text-[#3B5A3E]"}`}
        >
          {u.label}
        </button>
      ))}
    </div>
  );
}

function StatsRow({ summary, unit, onSetGoal }) {
  if (!summary?.has_data) {
    return (
      <div className="ff-card p-6 text-center" data-testid="weight-empty-state">
        <div className="text-3xl mb-2">⚖️</div>
        <div className="text-base font-semibold text-[#2A332C]">No entries yet</div>
        <p className="text-sm text-[#5C6B61] mt-1">Log your first weight below to start your journey.</p>
      </div>
    );
  }
  const goingDown = summary.total_change_kg < 0;
  const projColour = "#3B5A3E";
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="weight-stats-row">
      <Stat
        label="Current"
        value={formatDisplay(summary.current_kg, unit)}
        testid="stat-current"
      />
      <Stat
        label="Total change"
        value={formatDelta(summary.total_change_kg, unit)}
        accent={goingDown ? "#3B5A3E" : "#C36952"}
        sub={`from ${formatDisplay(summary.starting_kg, unit)}`}
        testid="stat-change"
      />
      <Stat
        label="Weekly avg"
        value={summary.weekly_avg_change_kg !== 0 ? formatDelta(summary.weekly_avg_change_kg, unit) : "—"}
        sub="last 4 weeks"
        testid="stat-weekly"
      />
      <Stat
        label={summary.streak_days >= 7 ? "🔥 Streak" : "Streak"}
        value={`${summary.streak_days} day${summary.streak_days === 1 ? "" : "s"}`}
        accent={summary.streak_days >= 3 ? "#C36952" : undefined}
        testid="stat-streak"
      />
      <div className="col-span-2 md:col-span-4 ff-card p-4 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="ff-label">Goal</div>
          <div className="mt-1 text-sm text-[#2A332C]">
            {summary.goal_kg ? (
              <>Target <strong>{formatDisplay(summary.goal_kg, unit)}</strong>
                {summary.projected_goal_date && (
                  <span className="text-[#5C6B61]">
                    {" "}— projected <strong style={{ color: projColour }}>
                      {new Date(summary.projected_goal_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                    </strong> at your current trend
                  </span>
                )}
              </>
            ) : (
              <span className="text-[#5C6B61]">No goal yet — set one to see your projected achievement date.</span>
            )}
          </div>
        </div>
        <button
          onClick={onSetGoal}
          data-testid="set-weight-goal-btn"
          className="px-4 py-2 rounded-full text-xs font-semibold text-white bg-[#3B5A3E] hover:bg-[#2A332C] transition"
        >
          {summary.goal_kg ? "Edit goal" : "Set goal"}
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, accent, testid }) {
  return (
    <div className="ff-card p-4" data-testid={testid}>
      <div className="ff-label">{label}</div>
      <div className="text-xl font-semibold mt-1" style={{ color: accent || "#2A332C", fontFamily: "Outfit, sans-serif" }}>
        {value}
      </div>
      {sub && <div className="text-[11px] text-[#8B9690] mt-1">{sub}</div>}
    </div>
  );
}

function LogForm({ unit, onLogged }) {
  const [value, setValue] = useState("");
  const [date, setDate] = useState(todayDateStr());
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const num = parseFloat(value);
    if (!num || num <= 0) {
      toast.error("Enter a valid weight");
      return;
    }
    const kg = fromDisplay(num, unit);
    if (kg < 25 || kg > 400) {
      toast.error("That weight looks off — please double-check the unit");
      return;
    }
    setSaving(true);
    try {
      // Compose an ISO datetime — keep noon UTC so the entry sits cleanly on the chosen date.
      const isoDate = new Date(`${date}T12:00:00Z`).toISOString();
      await api.post("/weight-log", {
        weight_kg: Number(kg.toFixed(2)),
        display_unit: unit,
        logged_at: isoDate,
        note: note.trim() || undefined,
      });
      toast.success("Weight logged");
      setValue("");
      setNote("");
      onLogged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not log");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ff-card p-4 md:p-6 space-y-3" data-testid="weight-log-form">
      <div className="ff-label">Log a weight</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 items-end">
        <div className="col-span-1 sm:col-span-1">
          <label className="text-xs text-[#5C6B61]">Weight ({unit})</label>
          <input
            type="number"
            step="0.1"
            inputMode="decimal"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={unit === "kg" ? "75.4" : unit === "lb" ? "166.2" : "11.9"}
            data-testid="weight-input"
            className="w-full mt-1 px-3 py-2 rounded-xl border border-[#E5E2DA] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3B5A3E]/30"
          />
        </div>
        <div>
          <label className="text-xs text-[#5C6B61]">Date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            max={todayDateStr()}
            data-testid="weight-date"
            className="w-full mt-1 px-3 py-2 rounded-xl border border-[#E5E2DA] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3B5A3E]/30"
          />
        </div>
        <div className="md:col-span-1">
          <label className="text-xs text-[#5C6B61]">Note (optional)</label>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={140}
            placeholder="post-workout"
            data-testid="weight-note"
            className="w-full mt-1 px-3 py-2 rounded-xl border border-[#E5E2DA] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3B5A3E]/30"
          />
        </div>
        <button
          onClick={submit}
          disabled={saving || !value}
          data-testid="weight-log-btn"
          className="h-[42px] px-4 rounded-full text-sm font-semibold text-white bg-[#3B5A3E] hover:bg-[#2A332C] transition disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving ? "Saving…" : "Log weight"}
        </button>
      </div>
    </div>
  );
}

function HistoryList({ logs, unit, onChanged }) {
  if (logs.length === 0) return null;
  const sorted = [...logs].reverse(); // newest first
  return (
    <div className="ff-card p-4 md:p-6" data-testid="weight-history">
      <div className="ff-label mb-3">History · {logs.length} entries</div>
      <ul className="divide-y divide-[#F0EDE7]">
        {sorted.slice(0, 50).map((l) => (
          <WeightRow key={l.log_id} log={l} unit={unit} onDeleted={onChanged} />
        ))}
      </ul>
      {sorted.length > 50 && (
        <div className="text-xs text-[#8B9690] mt-3 text-center">Showing the latest 50 entries.</div>
      )}
    </div>
  );
}

function WeightRow({ log, unit, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    if (!confirm("Delete this entry?")) return;
    setBusy(true);
    try {
      await api.delete(`/weight-log/${log.log_id}`);
      toast.success("Deleted");
      onDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
      setBusy(false);
    }
  };
  return (
    <li className="py-3 flex items-center gap-3" data-testid={`weight-row-${log.log_id}`}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-[#2A332C]">{formatDisplay(log.weight_kg, unit)}</div>
        <div className="text-[11px] text-[#8B9690] mt-0.5">
          {new Date(log.logged_at).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
          {log.note && <span> · <em>{log.note}</em></span>}
        </div>
      </div>
      <button
        onClick={remove}
        disabled={busy}
        data-testid={`weight-delete-${log.log_id}`}
        aria-label="Delete entry"
        className="text-[11px] text-[#A65541] hover:underline disabled:opacity-40"
      >
        Delete
      </button>
    </li>
  );
}

function GoalModal({ current, unit, onClose, onSaved }) {
  const [value, setValue] = useState(() => current ? String(toDisplay(current, unit).toFixed(1)) : "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (value === "" || value === null) {
      // Clear goal
      setSaving(true);
      try {
        await api.put("/weight-log/goal", { goal_kg: null });
        toast.success("Goal cleared");
        onSaved();
        onClose();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not clear");
        setSaving(false);
      }
      return;
    }
    const num = parseFloat(value);
    if (!num || num <= 0) {
      toast.error("Enter a valid target weight");
      return;
    }
    const kg = fromDisplay(num, unit);
    if (kg < 25 || kg > 400) {
      toast.error("That target looks off — please double-check the unit");
      return;
    }
    setSaving(true);
    try {
      await api.put("/weight-log/goal", { goal_kg: Number(kg.toFixed(2)) });
      toast.success("Goal saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4" data-testid="weight-goal-modal" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-white rounded-2xl p-6 shadow-xl">
        <h2 className="text-xl font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>Set weight goal</h2>
        <p className="text-sm text-[#5C6B61] mt-2">We&apos;ll use this to project when you&apos;ll hit your target based on your current trend.</p>
        <div className="mt-4">
          <label className="text-xs text-[#5C6B61]">Target weight ({unit})</label>
          <input
            type="number"
            step="0.1"
            inputMode="decimal"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={unit === "kg" ? "70" : unit === "lb" ? "154" : "11"}
            data-testid="weight-goal-input"
            className="w-full mt-1 px-3 py-2 rounded-xl border border-[#E5E2DA] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3B5A3E]/30"
          />
        </div>
        <div className="mt-5 flex items-center gap-2 flex-wrap">
          <button
            onClick={save}
            disabled={saving}
            data-testid="weight-goal-save"
            className="px-4 py-2 rounded-full text-xs font-semibold text-white bg-[#3B5A3E] hover:bg-[#2A332C] transition disabled:opacity-40"
          >
            {saving ? "Saving…" : "Save goal"}
          </button>
          {current != null && (
            <button
              onClick={() => { setValue(""); }}
              disabled={saving}
              className="px-3 py-2 rounded-full text-xs text-[#A65541] hover:bg-[#FCF6F2] transition"
            >
              Clear
            </button>
          )}
          <button
            onClick={onClose}
            disabled={saving}
            data-testid="weight-goal-cancel"
            className="ml-auto px-3 py-2 rounded-full text-xs text-[#5C6B61] hover:bg-[#F3F0EA] transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function ProLockedChartCard() {
  const triggerUpsell = () => {
    try {
      window.dispatchEvent(new CustomEvent("pro-upsell", { detail: {
        code: "pro_chart",
        message: "See your full progress chart, goal line, and projected goal date with Pro.",
        feature: "Progress chart + projection",
      } }));
    } catch { /* ignore */ }
  };
  return (
    <div
      onClick={triggerUpsell}
      role="button"
      tabIndex={0}
      data-testid="weight-chart-locked"
      className="ff-card p-6 md:p-8 border border-[#E0D5C2] bg-gradient-to-br from-[#FAF6EE] to-[#F3F0EA] cursor-pointer hover:border-[#3B5A3E] transition relative overflow-hidden"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-[#3B5A3E] text-white flex items-center justify-center text-xl flex-none">📈</div>
        <div className="flex-1 min-w-0">
          <div className="ff-label" style={{ color: "#8A5C12" }}>Pro feature</div>
          <h3 className="text-lg font-semibold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
            See your full progress chart
          </h3>
          <p className="text-sm text-[#5C6B61] mt-2">
            Tap to unlock the visual progress chart with your goal line and projected goal date based on your trend.
          </p>
        </div>
      </div>
      <svg viewBox="0 0 400 60" className="w-full h-12 mt-4 opacity-40" preserveAspectRatio="none">
        <polyline fill="none" stroke="#3B5A3E" strokeWidth="2.5" points="0,40 50,35 100,32 150,28 200,30 250,22 300,18 350,15 400,10" />
      </svg>
    </div>
  );
}

function WhatsWorkingCard({ unit }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: d } = await api.get("/weight-log/insights");
        if (!cancelled) setData(d);
      } catch {
        // Silent fail — the card just won't appear if the endpoint errors.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;
  if (!data) return null;

  // Pro upsell state
  if (!data.available && data.reason === "pro_required") {
    return (
      <div className="ff-card p-5 md:p-6 border border-[#E0D5C2] bg-gradient-to-br from-[#FAF6EE] to-[#F3F0EA] flex items-start gap-4" data-testid="whats-working-pro-upsell">
        <div className="w-12 h-12 rounded-2xl bg-[#3B5A3E] text-white flex items-center justify-center text-xl flex-none">💡</div>
        <div className="flex-1 min-w-0">
          <div className="ff-label" style={{ color: "#8A5C12" }}>Pro insight</div>
          <h3 className="text-lg font-semibold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
            What&apos;s actually driving your loss?
          </h3>
          <p className="text-sm text-[#5C6B61] mt-2">
            Pro unlocks a personalised &ldquo;What&apos;s working?&rdquo; card that compares your home-cooked weeks vs your scan / takeaway weeks and tells you which habit is actually moving the scales.
          </p>
          <a
            href="/app/billing"
            data-testid="whats-working-upgrade-btn"
            className="inline-block mt-4 px-4 py-2 rounded-full text-xs font-semibold text-white bg-[#3B5A3E] hover:bg-[#2A332C] transition"
          >
            Upgrade to Pro
          </a>
        </div>
      </div>
    );
  }

  // Not enough data state
  if (!data.available) {
    return (
      <div className="ff-card p-5 md:p-6" data-testid="whats-working-empty">
        <div className="ff-label">What&apos;s working?</div>
        <p className="text-sm text-[#5C6B61] mt-2">{data.message}</p>
      </div>
    );
  }

  // Active insight
  const high = data.high_home_cook_weeks || {};
  const low = data.low_home_cook_weeks || {};

  return (
    <div className="ff-card p-5 md:p-6 border border-[#D4E0D5] bg-gradient-to-br from-[#F4F8F4] to-white" data-testid="whats-working-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-2xl bg-[#3B5A3E] text-white flex items-center justify-center text-lg flex-none">💡</div>
        <div className="flex-1 min-w-0">
          <div className="ff-label" style={{ color: "#3B5A3E" }}>What&apos;s working?</div>
          <h3 className="text-lg font-semibold mt-1" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="insight-headline">
            {data.headline}
          </h3>
          <p className="text-sm text-[#3C4842] mt-2" data-testid="insight-verdict">{data.verdict}</p>

          <div className="grid grid-cols-2 gap-3 mt-4">
            <InsightBucket
              label="Home-cooked weeks"
              sublabel="≥60% recipes"
              count={high.count}
              avgChange={high.avg_change_kg}
              unit={unit}
              accent="#3B5A3E"
            />
            <InsightBucket
              label="Scan-heavy weeks"
              sublabel="<60% recipes"
              count={low.count}
              avgChange={low.avg_change_kg}
              unit={unit}
              accent="#C36952"
            />
          </div>

          <div className="text-[11px] text-[#8B9690] mt-4">
            Based on {data.weeks_analysed} {data.weeks_analysed === 1 ? "week" : "weeks"} of paired weight + meal data.
          </div>
        </div>
      </div>
    </div>
  );
}

function InsightBucket({ label, sublabel, count, avgChange, unit, accent }) {
  const display = avgChange == null
    ? "—"
    : formatDelta(avgChange, unit);
  return (
    <div className="rounded-2xl bg-white border border-[#E5E2DA] p-3">
      <div className="ff-label">{label}</div>
      <div className="text-[10px] text-[#8B9690] mt-0.5">{sublabel}</div>
      <div
        className="text-xl font-semibold mt-2"
        style={{ color: accent, fontFamily: "Outfit, sans-serif" }}
      >
        {display}
      </div>
      <div className="text-[11px] text-[#5C6B61] mt-1">
        {count ?? 0} {count === 1 ? "week" : "weeks"} · avg weekly change
      </div>
    </div>
  );
}

